// Fetch wrappers for the herzensmomente API. All requests are same-origin with
// cookies; errors normalize to ApiError so UI code can switch on `status`/`code`.

export class ApiError extends Error {
	constructor(status, code) {
		super(`api ${status}: ${code}`);
		this.status = status;
		this.code = code;
	}
}

// Anonymous per-device id, sent with uploads, listings and deletes. The server
// stores it on every guest upload, so the same device can later delete its own
// uploads again ("Ups, das wollte ich nicht hochladen").
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function randomUuid() {
	if (crypto.randomUUID) return crypto.randomUUID();
	// Old Safari (< 15.4) — build a v4 UUID from getRandomValues.
	const b = crypto.getRandomValues(new Uint8Array(16));
	b[6] = (b[6] & 0x0f) | 0x40;
	b[8] = (b[8] & 0x3f) | 0x80;
	const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

let clientIdCache = null;
export function getClientId() {
	if (clientIdCache) return clientIdCache;
	try {
		const stored = localStorage.getItem('hm_client');
		clientIdCache = stored && UUID_RE.test(stored) ? stored : randomUuid();
		if (stored !== clientIdCache) localStorage.setItem('hm_client', clientIdCache);
	} catch {
		clientIdCache = randomUuid(); // storage blocked — the id lives for this tab only
	}
	return clientIdCache;
}

async function request(path, { body, headers, method = 'GET' } = {}) {
	const res = await fetch(path, {
		method,
		credentials: 'include',
		headers: body ? { 'Content-Type': 'application/json', ...headers } : headers,
		body: body ? JSON.stringify(body) : undefined,
	});
	if (!res.ok) {
		let code = 'unknown';
		try {
			code = (await res.json()).error ?? 'unknown';
		} catch {
			/* non-JSON error body */
		}
		throw new ApiError(res.status, code);
	}
	return res.status === 204 ? null : res.json();
}

// Upload caps, learned from /api/auth/me at boot so the uploader can turn away an
// oversized video before it spends twenty minutes on a phone uplink. The fallbacks
// only ever apply if that call failed — the server enforces the real numbers.
let limitsCache = null;
export const getLimits = () => limitsCache ?? { imageBytes: 50 * 1024 * 1024, videoBytes: 2000 * 1024 * 1024 };

export const api = {
	me: async () => {
		const me = await request('/api/auth/me');
		if (me?.limits) limitsCache = me.limits;
		return me;
	},
	login: (password) => request('/api/auth/login', { method: 'POST', body: { password } }),
	linkLogin: (token) => request('/api/auth/link', { method: 'POST', body: { token } }),
	logout: () => request('/api/auth/logout', { method: 'POST' }),
	adminLogin: (user, password) => request('/api/auth/admin/login', { method: 'POST', body: { user, password } }),
	adminLogout: () => request('/api/auth/admin/logout', { method: 'POST' }),

	categories: () => request('/api/categories'),
	createCategory: (data) => request('/api/categories', { method: 'POST', body: data }),
	updateCategory: (id, patch) => request(`/api/categories/${id}`, { method: 'PATCH', body: patch }),
	deleteCategory: (id, force) => request(`/api/categories/${id}${force ? '?force=1' : ''}`, { method: 'DELETE' }),

	checkHashes: (category, hashes) => request('/api/media/check', { method: 'POST', body: { category, hashes } }),
	deleteMedia: (id) => request(`/api/media/${id}`, { method: 'DELETE', headers: { 'X-Client-Id': getClientId() } }),
	moveMedia: (id, category) => request(`/api/media/${id}`, { method: 'PATCH', body: { category } }),
	retryMedia: (id) => request(`/api/media/${id}/retry`, { method: 'POST' }),
	stats: () => request('/api/stats'),
};

// Conditional listing: the gallery polls every 20s; the server answers 304 for an
// unchanged revision so idle polling costs nothing and triggers no re-render.
export async function fetchMedia(category, etag) {
	const res = await fetch(`/api/media?category=${encodeURIComponent(category)}`, {
		credentials: 'include',
		// X-Client-Id lets the server mark the requester's own uploads (`mine`).
		headers: { 'X-Client-Id': getClientId(), ...(etag ? { 'If-None-Match': etag } : {}) },
	});
	if (res.status === 304) return { notModified: true };
	if (!res.ok) throw new ApiError(res.status, 'media_list_failed');
	const data = await res.json();
	return { ...data, etag: res.headers.get('ETag') };
}

// Single-file upload via XHR — fetch still has no upload progress events.
// Resolves { duplicate: true } or the created record; rejects with ApiError.
export function uploadFile(file, category, { onProgress, signal } = {}) {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open('POST', '/api/media');
		xhr.withCredentials = true;
		xhr.setRequestHeader('X-Client-Id', getClientId());
		xhr.upload.onprogress = (e) => {
			if (e.lengthComputable) onProgress?.(e.loaded, e.total);
		};
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				try {
					resolve(JSON.parse(xhr.responseText));
				} catch {
					resolve({});
				}
			} else {
				let code = 'unknown';
				try {
					code = JSON.parse(xhr.responseText).error ?? 'unknown';
				} catch {
					/* non-JSON body */
				}
				reject(new ApiError(xhr.status, code));
			}
		};
		xhr.onerror = () => reject(new ApiError(0, 'network'));
		xhr.onabort = () => reject(new ApiError(0, 'aborted'));
		signal?.addEventListener('abort', () => xhr.abort());
		const fd = new FormData();
		fd.append('category', category);
		fd.append('file', file);
		xhr.send(fd);
	});
}

// URL builders — the listing payload carries only ids; every asset URL derives
// from id + type, which keeps thousand-item responses tiny.
export const thumbUrl = (item) => `/media/thumbs/${item.id}.jpg`;
export const displayUrl = (item) => `/media/display/${item.id}.${item.type === 'video' ? 'mp4' : 'jpg'}`;
export const posterUrl = (item) => `/media/display/${item.id}.poster.jpg`;
export const downloadUrl = (item) => `/api/media/${item.id}/original`;
export const categoryZipUrl = (categoryId) => `/api/zip/category/${encodeURIComponent(categoryId)}`;

// ZIP of a selection: submit a hidden form so the browser handles the response as
// a native streaming download — fetch()+Blob would buffer gigabytes in JS memory.
export function downloadZip(ids) {
	const form = document.createElement('form');
	form.method = 'POST';
	form.action = '/api/zip';
	form.style.display = 'none';
	const input = document.createElement('input');
	input.type = 'hidden';
	input.name = 'ids';
	input.value = ids.join(',');
	form.appendChild(input);
	document.body.appendChild(form);
	form.submit();
	form.remove();
}
